import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useP } from '../i18n/useP';
import {
  ComposedChart, Bar, LineChart, Line, LabelList, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import { getBsView } from '../api/financials_bs';
import { getPlView } from '../api/financials_pl';
import { formatNumber } from '../utils/format';
import Card from '../components/Card';
import { buildWorkingCapitalModel, M3 } from './financials/workingCapitalModel';
import './FinancialsWorkingCapitalTab.css';

/* Hub Liquidités — trois onglets, un seul modèle :
   • Synthèse  : graphique 12 mois roulants + tuiles (Trésorerie+BFRE, DSO/DIO/DPO).
   • Liquidité : 3 degrés (1er = liquidités/DCT ; 2e = (liq.+créances)/DCT ;
     3e = actif circulant/DCT) · soldes mensuels YTD.
   • BFRE      : tables 12 MOIS ROULANT (passés). Tableau BFRE d'exploitation
     (composantes std_key → BFRE → liquidités → BFRE+liquidités) puis 3 rotations
     (DSO/DIO/DPO), chacune = flux 12 mois roulants + délai en jours. Base TTC
     (×(1+vat_rate)) pour DSO/DPO (créances/dettes TTC ÷ flux HT) ; DIO sans TVA.
   BFRE : comptes marqués « BFRE » en Structure Bilan ; signe par section
   (passif − / actif +). Identité des comptes (créances/stocks/fournisseurs) par
   std_key figé. Trésorerie = lignes cf_category cash. */

/* section : 'synthesis' (graphique + tuiles) · 'liquidity' (3 degrés) ·
   'treasury' (Trésorerie + BFRE) · 'all' (tout, défaut). Le modèle est calculé
   une seule fois ; seules les blocs demandés sont rendus — un même calcul sert
   les trois onglets du hub Liquidités. */
/* `forecastView` — BFRE et ratios sur 12 mois, adossés au bilan hybride
   (réel ≤ clôture, prévision au-delà). Mêmes formules que la vue réelle. */
export default function FinancialsWorkingCapitalTab({ activeTenant, year, section = 'all', unit = 'chf', onReady, sideChart = null, fcf = null, forecastView = false, cutoff = null }) {
  const { t } = useTranslation();
  const p = useP();
  const tenantId = activeTenant?.id || null;
  const [bs, setBs] = useState(null);
  const [bsPrev, setBsPrev] = useState(null);
  const [bsPrev2, setBsPrev2] = useState(null);
  const [pl, setPl] = useState(null);
  const [plPrev, setPlPrev] = useState(null);
  const [plPrev2, setPlPrev2] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openFormula, setOpenFormula] = useState(null);   // ligne formule (onglet BFRE)
  const [openRot, setOpenRot] = useState(null);            // bloc rotation déplié (valeurs du calcul)

  useEffect(() => {
    if (!tenantId || !year) {
      setBs(null); setBsPrev(null); setBsPrev2(null); setPl(null); setPlPrev(null); setPlPrev2(null); setLoading(false);
      if (onReady) onReady();
      return;
    }
    let alive = true;
    setLoading(true);
    // bs sur 3 ans (soldes) + pl sur 3 ans : base 12 mois roulants des rotations
    // (DSO/DIO/DPO). Numérateur = solde MOYEN 12 mois → exige l'historique N-2.
    Promise.all([
      // `cutoff` : arrêté d'affichage (Rapports, vue Réel) — année courante seule.
      getBsView(tenantId, year, { ...(forecastView ? { flatForecast: true } : {}), ...(cutoff ? { through: cutoff } : {}) }),
      getBsView(tenantId, year - 1).catch(() => null),
      getBsView(tenantId, year - 2).catch(() => null),
      getPlView(tenantId, year, cutoff ? { through: cutoff } : {}).catch(() => null),
      getPlView(tenantId, year - 1).catch(() => null),
      getPlView(tenantId, year - 2).catch(() => null),
    ]).then(([bsR, bsPrevR, bsPrev2R, plR, plPrevR, plPrev2R]) => {
      if (!alive) return;
      setBs(bsR?.data || null);
      setBsPrev(bsPrevR?.data || null);
      setBsPrev2(bsPrev2R?.data || null);
      setPl(plR?.data || null);
      setPlPrev(plPrevR?.data || null);
      setPlPrev2(plPrev2R?.data || null);
      setLoading(false);
      if (onReady) onReady();
    }).catch(() => { if (alive) { setLoading(false); if (onReady) onReady(); } });
    return () => { alive = false; };
  }, [tenantId, year, forecastView, cutoff]);

  const model = useMemo(
    () => buildWorkingCapitalModel({ bs, bsPrev, bsPrev2, pl, plPrev, plPrev2, year, activeTenant,
      horizon: forecastView ? 12 : undefined }),
    [bs, bsPrev, bsPrev2, pl, plPrev, plPrev2, year, activeTenant, forecastView],
  );

  // Classe `-loading` comme partout ailleurs : sans elle, rien ne distinguait
  // cet onglet d'un onglet muet pendant l'export (il en manquait deux au
  // classeur des Rapports : BFRE et ratio de liquidité).
  if (loading) return <p className="fwc-loading">Chargement du fond de roulement…</p>;
  if (!model) return <p style={{ color: 'var(--color-text-muted)' }}>{p('Aucun bilan pour cette année.')}</p>;

  const {
    closed, hasBfre, liqValues, liqRatios, rolling, bfreReport, vatMult,
  } = model;
  if (closed < 1) return <p style={{ color: 'var(--color-text-muted)' }}>{p('Aucun mois clôturé sur cette année.')}</p>;

  const li = closed - 1;
  const k = unit === 'kchf' ? 1000 : 1;              // diviseur d'affichage (montants CHF)
  const cell = (v) => (Math.round(v) === 0 ? '—' : formatNumber(Math.round(v / k), 0));
  const ratioFmt = (v) => Number(v).toFixed(2);      // 1.54 (et non 154 %)
  const monthIdx = Array.from({ length: 12 }, (_, i) => i);

  // Grille de colonnes commune aux 2 tableaux (table-layout fixe) → mois alignés.
  // Colonne « Cible » toujours présente géométriquement : card valeurs = libellé
  // en colSpan 2 (la cible reste vide), card ratios = vraie colonne Cible.
  const prevYear = year - 1;

  const LiqColgroup = () => (
    <colgroup>
      <col className="fwc-col-label" />
      <col className="fwc-col-cible" />
      <col className="fwc-col-open" />
      {monthIdx.map((i) => <col key={i} className="fwc-col-month" />)}
    </colgroup>
  );

  const LiqRow = ({ r, cible }) => {
    const show = (v) => (r.fmt === 'ratio' ? ratioFmt(v) : cell(v));
    return (
      <tr className={r.cls}>
        {cible ? (
          <>
            <td className="fwc-rowhead">{r.label}{r.sub && <span className="fwc-sub">{r.sub}</span>}</td>
            <td className="fwc-cible">{r.cible ? `[${r.cible}]` : ''}</td>
          </>
        ) : (
          <td className="fwc-rowhead" colSpan={2}>{r.label}{r.sub && <span className="fwc-sub">{r.sub}</span>}</td>
        )}
        <td className="fwc-open">{r.open == null ? '—' : show(r.open)}</td>
        {monthIdx.map((i) => {
          const empty = i >= closed || r.vals[i] == null;
          return (
            <td key={i} className={`${i >= closed ? 'fwc-future' : ''}${r.fmt === 'ratio' ? ' fwc-ratioval' : ''}`}>
              {empty ? '—' : show(r.vals[i])}
            </td>
          );
        })}
      </tr>
    );
  };

  const MonthHead = ({ cible }) => (
    <thead>
      {/* Bandeau standard RÉALISÉ / PRÉVISIONNEL (2026-08-20) — seulement
          quand la fenêtre mêle mois clos et mois à venir. */}
      {closed < monthIdx.length && (
        <tr className="fwc-band-row">
          <th colSpan={3} />
          {closed > 0 && (
            <th className="lk-band lk-band--real" colSpan={closed}>{t('finance:reports.bandReal')}</th>
          )}
          <th className="lk-band lk-band--fore" colSpan={monthIdx.length - closed}>{t('finance:reports.bandFore')}</th>
        </tr>
      )}
      <tr>
        {cible
          ? (<><th className="fwc-rowhead">&nbsp;</th><th className="fwc-cible">Cible</th></>)
          : (<th className="fwc-rowhead" colSpan={2}>&nbsp;</th>)}
        <th className="fwc-open">Déc. {String(prevYear).slice(2)}</th>
        {monthIdx.map((i) => <th key={i} className={i >= closed ? 'fwc-future' : ''}>{M3[i]}</th>)}
      </tr>
    </thead>
  );

  // Tuiles Synthèse : DSO/DIO/DPO (valeur en jours, sans détail de calcul — le
  // détail vit dans l'onglet BFRE).
  const DayTile = ({ rot }) => {
    const v = rot.days[rot.days.length - 1];
    return (
      <div className="fwc-tile">
        <div className="fwc-tile__label">{rot.daysLabel}</div>
        <div className="fwc-tile__val">{v == null ? '—' : Math.round(v)} <small>jours</small></div>
      </div>
    );
  };

  // Tables 12 mois roulants (onglet BFRE) — pas de colonne Variation YTD.
  // Largeur de colonnes fixe partagée → les mois s'alignent entre la table
  // principale BFRE et les cards rotation (libellés de longueurs différentes).
  const RollColgroup = () => (
    <colgroup>
      <col className="fwc-col-rolllabel" />
      {bfreReport.labels.map((_, i) => <col key={i} className="fwc-col-month" />)}
    </colgroup>
  );
  const RollHead = () => (
    <thead>
      <tr>
        <th className="fwc-rowhead">&nbsp;</th>
        {bfreReport.labels.map((l, i) => <th key={i}>{l}</th>)}
      </tr>
    </thead>
  );
  const rollCell = (v, fmt) => {
    if (v == null) return '—';
    if (fmt === 'days') return `${Math.round(v)} j`;
    if (fmt === 'turns') return `${v.toFixed(1)}x`;
    return cell(v);
  };
  const RollRow = ({ label, sub, vals, fmt, cls }) => (
    <tr className={cls}>
      <td className="fwc-rowhead">{label}{sub && <span className="fwc-sub">{sub}</span>}</td>
      {vals.map((v, i) => (
        <td key={i} className={!fmt && Math.round(v || 0) === 0 ? 'fwc-zero' : ''}>
          {rollCell(v, fmt)}
        </td>
      ))}
    </tr>
  );

  const show = (s) => section === 'all' || section === s;
  // Mode « graphique seul » : uniquement la courbe Suivi de la liquidité (sans
  // les tuiles de rotation) — réutilisé comme tuile-clé du Cockpit.
  const chartOnly = section === 'synthesis-chart';

  return (
    <>
      {(show('synthesis') || chartOnly) && (
      <>
        {/* Graphique « Suivi de la liquidité » ; côte à côte avec un graphique
            injecté (sideChart, ex. Pont de trésorerie) quand il est fourni. */}
        <div className={sideChart ? 'fwc-charts-row' : undefined}>
        <Card title={t('phrases:p_f8afbebf23')} subtitle={p("Courbe = tr\u00e9sorerie + cr\u00e9ances clients + stocks \u2212 dettes fournisseurs \u00b7 barres : tr\u00e9sorerie + cr\u00e9ances clients + stocks (au-dessus de 0), dettes fournisseurs (sous 0) \u00b7 traitill\u00e9 = charge de personnel mensuelle")} className="fwc-chartcard">
          <div className="fwc-head__chartbox">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={rolling} margin={{ top: 18, right: 14, left: 6, bottom: 0 }}>
                {/* Trame diagonale pour la Trésorerie (distinction non-chromatique, daltoniens). */}
                <defs>
                  <pattern id="fwcTresoHatch" patternUnits="userSpaceOnUse" width="7" height="7" patternTransform="rotate(45)">
                    <rect width="7" height="7" fill="#C7D8EF" />
                    <line x1="0" y1="0" x2="0" y2="7" stroke="#7C9BCB" strokeWidth="2.4" />
                  </pattern>
                </defs>
                <CartesianGrid stroke="#EDE8DF" vertical={false} />
                <XAxis dataKey="m" tick={{ fontSize: 11, fill: '#78716C' }} axisLine={false} tickLine={false} interval="preserveStartEnd" />
                <YAxis tick={{ fontSize: 11, fill: '#78716C' }} axisLine={false} tickLine={false} width={70}
                  tickFormatter={(v) => formatNumber(Math.round(v / k), 0)} />
                <Tooltip formatter={(v, n) => [formatNumber(Math.round(v / k), 0), n]} labelStyle={{ color: '#2B203E' }} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                {/* Pile : Trésorerie (bleu tramé) + Créances clients (ambre) + Stocks (sage)
                    au-dessus de 0, Dettes fournisseurs en négatif sous 0. Distinction non
                    chromatique (trame sur la trésorerie) pour daltoniens. */}
                <Bar dataKey="treso" name={t('phrases:p_e0e75da7e7')} stackId="pos" fill="url(#fwcTresoHatch)" stroke="#7C9BCB" strokeWidth={0.5} isAnimationActive={false} maxBarSize={26} />
                <Bar dataKey="creances" name={t('phrases:p_036e9d357b')} stackId="pos" fill="#E3C485" isAnimationActive={false} maxBarSize={26} />
                <Bar dataKey="stock" name="Stocks" stackId="pos" fill="#8DC076" isAnimationActive={false} maxBarSize={26} />
                <Bar dataKey="fourn" name="Dettes fournisseurs" stackId="neg" fill="#B7A6C4" isAnimationActive={false} maxBarSize={26} />
                {/* Courbe nette = trésorerie + créances clients + stocks − dettes fournisseurs, valeurs affichées. */}
                <Line type="monotone" dataKey="net" name={t('phrases:p_f26cb24ead')} stroke="#2B203E" strokeWidth={2.4} dot={{ r: 2, fill: '#2B203E' }} connectNulls isAnimationActive={false}>
                  <LabelList dataKey="net" position="top" offset={8}
                    formatter={(v) => (v == null ? '' : formatNumber(Math.round(v / k), 0))}
                    style={{ fontSize: 9.5, fill: '#2B203E', fontWeight: 600 }} />
                </Line>
                {/* Référence : charge de personnel mensuelle (traitillé fin). */}
                <Line type="monotone" dataKey="perso" name={p('Charge de personnel')} stroke="#BA5D4A" strokeWidth={1.4} strokeDasharray="4 3" dot={false} connectNulls isAnimationActive={false} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </Card>
        {!chartOnly && sideChart}
        </div>
        {/* Bande 3 : génération de cash (Free cash flow, injecté depuis le hub)
            + rotations DSO/DIO/DPO. La trésorerie actuelle vit dans le bandeau KPI.
            Masquée en mode graphique seul (tuile du Cockpit). */}
        {!chartOnly && (
        <div className="fwc-tiles">
          {fcf != null && (
            <div className="fwc-tile">
              <div className="fwc-tile__label">Free cash flow</div>
              <div className="fwc-tile__val">{cell(fcf)} <small>{unit === 'kchf' ? 'kCHF' : 'CHF'}</small></div>
            </div>
          )}
          {bfreReport.rotations.map((rot) => <DayTile key={rot.key} rot={rot} />)}
        </div>
        )}
      </>
      )}

      {show('liquidity') && (
      <>
        <Card title={t('phrases:p_77accebaee')} flush>
          <div className="fwc-wrap">
            <table className="fwc-table fwc-table--liq">
              <LiqColgroup />
              <MonthHead />
              <tbody>
                {liqValues.map((r, k) => <LiqRow key={k} r={r} />)}
              </tbody>
            </table>
          </div>
        </Card>
        <Card title={t('phrases:p_f6ce00713c')} flush>
          <div className="fwc-wrap">
            <table className="fwc-table fwc-table--liq">
              <LiqColgroup />
              <MonthHead cible />
              <tbody>
                {liqRatios.map((r, k) => <LiqRow key={k} r={r} cible />)}
              </tbody>
            </table>
          </div>
        </Card>
      </>
      )}

      {show('treasury') && !hasBfre && (
        <Card title="Besoin en fonds de roulement d'exploitation" flush>
          <p style={{ color: 'var(--color-text-muted)', fontSize: 13, margin: '14px 18px' }}>
            Aucun compte inclus dans le BFRE. Active le jeton <strong>BFRE</strong> sur les lignes
            voulues dans Paramètres → Structure Bilan.
          </p>
        </Card>
      )}

      {show('treasury') && hasBfre && (
      <Card title="Besoin en fonds de roulement d'exploitation"
            subtitle={p('12 mois glissants')} flush>
        <div className="fwc-wrap">
          <table className="fwc-table fwc-table--roll">
            <RollColgroup />
            <RollHead />
            <tbody>
              {bfreReport.comps.map((c, i) => (
                <RollRow key={i} label={c.label} vals={c.vals} fmt="chf" cls="fwc-row--comp" />
              ))}
              <RollRow label="BFRE" vals={bfreReport.bfre} fmt="chf" cls="fwc-row--bfre" />
              <RollRow label={t('phrases:p_ad7d8bcd38')} vals={bfreReport.treso} fmt="chf" cls="fwc-row--treso" />
              <RollRow label={t('phrases:p_b99b8f862b')} vals={bfreReport.bfreTreso} fmt="chf" cls="fwc-row--total" />
            </tbody>
          </table>
        </div>
      </Card>
      )}

      {/* Délais & rotations (DSO/DIO/DPO) — chaque bloc est introduit par une
          bande teintée (ardoise clair) avec icône, cliquable : le clic déplie
          les VALEURS qui entrent dans le calcul (numérateur moyen 12 mois +
          flux 12 mois glissants). L'ⓘ affiche la formule dans une ligne de
          table (lisible, plus de popover tronqué par le scroll de la card). */}
      {show('treasury') && hasBfre && bfreReport.rotations.length > 0 && (
        <Card title={p('Délais & rotations')} subtitle={p('12 mois glissants')} flush className="fwc-rollcard">
          <div className="fwc-wrap">
            <table className="fwc-table fwc-table--roll">
              <RollColgroup />
              <RollHead />
              <tbody>
                {bfreReport.rotations.map((rot) => (
                  <React.Fragment key={rot.key}>
                    <tr className="fwc-row--secttitle fwc-row--rot"
                      onClick={() => setOpenRot(openRot === rot.key ? null : rot.key)}
                      title={openRot === rot.key ? p('Cliquer pour masquer le détail du calcul') : p('Cliquer pour voir le détail du calcul')}>
                      <td className="fwc-secttitle" colSpan={bfreReport.labels.length + 1}>
                        <RotIcon k={rot.key} />
                        <span className="fwc-secttitle__t">{rot.daysLabel}</span>
                        <svg className={`fwc-rot-chevron${openRot === rot.key ? ' open' : ''}`} viewBox="0 0 16 16" width="12" height="12" aria-hidden="true">
                          <polyline points="4 6 8 10 12 6" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        <span className="fwc-info">
                          <button type="button" className="fwc-info__btn" aria-label="Formule de calcul"
                            onClick={(e) => { e.stopPropagation(); setOpenFormula(openFormula === rot.key ? null : rot.key); }}>i</button>
                        </span>
                      </td>
                    </tr>
                    {openFormula === rot.key && (
                      <tr className="fwc-row--formula"><td colSpan={bfreReport.labels.length + 1}>
                        {rot.formula.split('·').map((line, i) => <span key={i} className="fwc-formula-line">{line.trim()}</span>)}
                      </td></tr>
                    )}
                    {rot.ttc && vatMult == null && (
                      <tr><td className="fwc-warn" colSpan={bfreReport.labels.length + 1}>
                        {p('Taux de TVA du tenant absent — délai non calculé (pas de repli silencieux).')}
                      </td></tr>
                    )}
                    <RollRow label={t('phrases:p_8c106e9fd5')} vals={rot.days} fmt="days" cls="fwc-row--liqval" />
                    {rot.acompteDays && (
                      <RollRow label={t('phrases:p_8dc37bf98f')} vals={rot.acompteDays} fmt="days" cls="fwc-row--liqval" />
                    )}
                    <RollRow label="Rotation (×/an)" vals={rot.turns} fmt="turns" cls="fwc-row--liqrat" />
                    {openRot === rot.key && (rot.details || []).map((d, i) => (
                      <RollRow key={i} label={d.label} vals={d.vals} fmt="chf" cls="fwc-row--rotdetail" />
                    ))}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </>
  );
}

// Icône par délai de rotation (SVG inline, couleur héritée — accent ardoise) :
// DSO = facture client · DIO = carton de stock · DPO = carte de paiement.
function RotIcon({ k }) {
  const common = {
    width: 15, height: 15, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor',
    strokeWidth: 1.8, strokeLinecap: 'round', strokeLinejoin: 'round',
    className: 'fwc-secticon', 'aria-hidden': true,
  };
  if (k === 'dso') {
    return (
      <svg {...common}>
        <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
        <path d="M14 3v5h5" />
        <path d="M9 13h6M9 17h4" />
      </svg>
    );
  }
  if (k === 'dio') {
    return (
      <svg {...common}>
        <path d="M21 8l-9-5-9 5v8l9 5 9-5z" />
        <path d="M3 8l9 5 9-5" />
        <path d="M12 13v8" />
      </svg>
    );
  }
  return (
    <svg {...common}>
      <rect x="2" y="5" width="20" height="14" rx="2" />
      <path d="M2 10h20" />
      <path d="M6 15h4" />
    </svg>
  );
}
